import React, { useMemo } from 'react';
import { DateUtils } from 'react-day-picker';
import { useLingui } from '@lingui/react/macro';

import { CalendarDirection } from '@rover/components/src/datetime/DatePicker';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { SchedulerFormProvider } from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/contexts/SchedulerFormContext';
import { OvernightDateRange } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import OneTimeRepeatToggleButtons from '@rover/shared/js/search/components/OneTimeRepeatToggleButtons';
import RepeatServiceScheduler from '@rover/shared/js/search/components/RepeatServiceScheduler';
import type { Day } from '@rover/types/src/datetime/Day';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import { type ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';

export type Props = {
  calendarDirection?: CalendarDirection;
  displayAs?: 'compact' | null | 'expanded';
  endPlaceholder: string;
  dayOfWeekLabel?: string;
  locale: Locale;
  maxDate?: Date;
  onChange: (arg0?: ServiceFrequencyEnum) => void;
  onDateRangeChange: (arg0: { startDate?: Date | null; endDate?: Date | null }) => void;
  oneTimeLabel: string;
  onSelectedDaysChange: (arg0: Day[]) => void;
  onStartDateChange: (arg0: {
    startDate?: Date | null;
    endDate?: Date | null;
    fireSearch: boolean;
  }) => void;
  repeatStartDateLabel: string;
  searchFilters: SearchFilters;
  serviceType?: ServiceType | null;
  showLabel: boolean;
  showArrow: boolean;
  startPlaceholder: string;
};

const DaytimeServiceScheduler = ({
  calendarDirection = CalendarDirection.FLEX,
  dayOfWeekLabel,
  displayAs,
  endPlaceholder,
  locale,
  maxDate = undefined,
  onChange,
  onDateRangeChange,
  oneTimeLabel,
  onSelectedDaysChange,
  onStartDateChange,
  repeatStartDateLabel,
  searchFilters,
  serviceType,
  showArrow,
  showLabel,
  startPlaceholder,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const handleRepeatServiceSchedulerStartDateChange = (selectedStartDate) => {
    const { endDate } = searchFilters;

    if (endDate) {
      const updatedEndDate = DateUtils.isDayAfter(selectedStartDate, endDate) ? undefined : endDate;
      return onStartDateChange({
        startDate: selectedStartDate,
        endDate: updatedEndDate,
        fireSearch: true,
      });
    }

    return onStartDateChange({
      startDate: selectedStartDate,
      endDate: undefined,
      fireSearch: true,
    });
  };

  const getFrequencyLabel = (service: ServiceType): string => {
    switch (service.slug) {
      case ServiceTypeSlug.DROP_IN:
        return t`How often do you need Drop-In Visits?`;
      case ServiceTypeSlug.DOGGY_DAY_CARE:
        return t`How often do you need Doggy Day Care?`;
      case ServiceTypeSlug.DOG_WALKING:
        return t`How often do you need Dog Walking?`;
      default:
        return t({
          message: `How often do you need ${service.name}?`,
          comment: 'How often do you need {serviceType}? e.g. Doggy Day Care, Dog Walking...',
        });
    }
  };

  const contextValue = useMemo(() => ({ isDirty: false, onFormDirtinessChanged: () => {} }), []);
  const { selectedDays, startDate, endDate, frequency } = searchFilters;

  return (
    <SchedulerFormProvider context={contextValue}>
      {serviceType && serviceType.name && (
        <OneTimeRepeatToggleButtons
          displayAs={displayAs}
          frequency={frequency}
          label={showLabel ? getFrequencyLabel(serviceType) : ''}
          onChange={onChange}
        />
      )}
      {frequency === 'onetime' ? (
        <OvernightDateRange
          calendarDirection={calendarDirection}
          endPlaceholder={endPlaceholder}
          label={oneTimeLabel}
          locale={locale}
          maxDate={maxDate}
          onChange={onDateRangeChange}
          startPlaceholder={startPlaceholder}
          searchFilters={{
            startDate,
            endDate,
          }}
          showArrow={showArrow}
        />
      ) : (
        <RepeatServiceScheduler
          dayOfWeekLabel={dayOfWeekLabel}
          locale={locale}
          maxDate={maxDate}
          onSelectedDaysChange={onSelectedDaysChange}
          onStartDateChange={handleRepeatServiceSchedulerStartDateChange}
          repeatStartDateLabel={repeatStartDateLabel}
          selectedDays={selectedDays || []}
          serviceTypeSlug={serviceType && serviceType.slug}
          showLabel={showLabel}
          startDate={startDate}
          startPlaceholder={startPlaceholder}
        />
      )}
    </SchedulerFormProvider>
  );
};

export default DaytimeServiceScheduler;
